/**
 * Zod validation for report generation and export preferences
 */

import { z } from 'zod';

export const reportFormatSchema = z.enum(['pdf', 'csv', 'excel', 'json']);

export const reportTypeSchema = z.enum([
  'monthly_financial_summary',
  'tax_deduction',
  'business_expense',
  'category_spending_analysis',
  'year_end_tax_summary',
  'profit_and_loss',
  'custom',
]);

export const reportDateRangePresetSchema = z.enum([
  'this_month',
  'last_month',
  'this_quarter',
  'last_quarter',
  'this_financial_year',
  'last_financial_year',
  'this_calendar_year',
  'last_calendar_year',
  'custom',
]);

export const reportFiltersSchema = z.object({
  categoryIds: z.array(z.string()).optional(),
  accountIds: z.array(z.string()).optional(),
  minAmount: z.number().nullable().optional(),
  maxAmount: z.number().nullable().optional(),
  keywords: z.string().nullable().optional(),
  dataSources: z.array(z.string()).optional(),
  chartTypes: z.array(z.string()).optional(),
});

export const reportGenerationInputSchema = z
  .object({
    title: z.string().trim().min(1, 'Report title is required'),
    reportType: reportTypeSchema,
    format: reportFormatSchema,
    dateRangePreset: reportDateRangePresetSchema,
    customStartDate: z.string().optional(),
    customEndDate: z.string().optional(),
    filters: reportFiltersSchema.optional(),
    includeCharts: z.boolean(),
    includeRawData: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (data.dateRangePreset === 'custom') {
      if (!data.customStartDate || !data.customEndDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Custom date range requires start and end dates',
          path: ['customStartDate'],
        });
        return;
      }
      if (data.customEndDate < data.customStartDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'End date must be on or after start date',
          path: ['customEndDate'],
        });
      }
    }
  });

export const exportPreferencesSchema = z
  .object({
    defaultFormat: reportFormatSchema.optional(),
    defaultDatePreset: reportDateRangePresetSchema.optional(),
    customStartDate: z.string().nullable().optional(),
    customEndDate: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.defaultDatePreset === 'custom') {
      if (!data.customStartDate || !data.customEndDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Custom preset requires start and end dates',
          path: ['customStartDate'],
        });
        return;
      }
      if (data.customEndDate < data.customStartDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'End date must be on or after start date',
          path: ['customEndDate'],
        });
      }
    }
  });

export type ReportGenerationInputValidated = z.infer<typeof reportGenerationInputSchema>;
export type ExportPreferencesValidated = z.infer<typeof exportPreferencesSchema>;
